import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, IconButton, Typography, Button, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight, Check, Close } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import {
  MarkMemberAbsent,
  MarkMemberAttendance,
  MemberAttendence,
} from "../../services/MembersService";

interface AttendanceEntry {
  id: string;
  userId: string;
  date: string;
  status?: "PRESENT" | "ABSENT";
}

interface AttendanceCalendarProps {
  memberId: string;
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const AttendanceCalendar: React.FC<AttendanceCalendarProps> = ({
  memberId,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [attendanceData, setAttendanceData] = useState<AttendanceEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchAttendance = useCallback(
    async (month: number, year: number) => {
      try {
        const data = await MemberAttendence({
          memberId,
          year,
          month,
        });
        setAttendanceData(data);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    },
    [memberId]
  );

  useEffect(() => {
    fetchAttendance(currentMonth.month(), currentMonth.year());
  }, [currentMonth, fetchAttendance]);

  /* ─── Attendance lookup set ─── */
  const attendanceByDate = useMemo(() => {
    const map = new Map<string, "PRESENT" | "ABSENT">();
    attendanceData.forEach((entry) => {
      const key = dayjs(entry.date).format("YYYY-MM-DD");
      const status = entry.status ?? "PRESENT";
      map.set(key, status);
    });
    return map;
  }, [attendanceData]);

  const isPresent = (date: Dayjs): boolean =>
    attendanceByDate.get(date.format("YYYY-MM-DD")) === "PRESENT";

  const isAbsent = (date: Dayjs): boolean => {
    return attendanceByDate.get(date.format("YYYY-MM-DD")) === "ABSENT";
  };

  /* ─── Calendar grid computation ─── */
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startDayOfWeek = startOfMonth.day(); // 0 = Sunday
    const daysInMonth = endOfMonth.date();

    const days: (Dayjs | null)[] = [];

    // Leading empty cells
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Actual days
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(currentMonth.date(d));
    }

    return days;
  }, [currentMonth]);

  /* ─── Handlers ─── */
  const handlePrevMonth = () =>
    setCurrentMonth((prev) => prev.subtract(1, "month"));

  const handleNextMonth = () => setCurrentMonth((prev) => prev.add(1, "month"));

  const handleToday = () => {
    setCurrentMonth(dayjs());
    setSelectedDate(dayjs());
  };

  const handleDayClick = (date: Dayjs) => {
    setSelectedDate(date);
  };

  const handleMarkPresent = async () => {
    if (!selectedDate) {
      return;
    }

    try {
      setIsSubmitting(true);
      await MarkMemberAttendance({
        memberId,
        date: selectedDate.format("YYYY-MM-DD"),
        status: "PRESENT",
      });
      await fetchAttendance(currentMonth.month(), currentMonth.year());
      enqueueSnackbar("Attendance marked as present", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error marking attendance as present:", error);
      enqueueSnackbar("Failed to mark attendance", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAbsent = async () => {
    if (!selectedDate) {
      return;
    }

    try {
      setIsSubmitting(true);
      await MarkMemberAbsent({
        memberId,
        date: selectedDate.format("YYYY-MM-DD"),
      });
      await fetchAttendance(currentMonth.month(), currentMonth.year());
      enqueueSnackbar("Attendance marked as absent", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error marking attendance as absent:", error);
      enqueueSnackbar("Failed to mark attendance", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ─── Day cell styling ─── */
  const getDayCellSx = (date: Dayjs) => {
    const isToday = date.isSame(dayjs(), "day");
    const isSelected =
      selectedDate !== null && date.isSame(selectedDate, "day");
    const present = isPresent(date);
    const absent = isAbsent(date);

    const base: Record<string, any> = {
      position: "relative",
      width: "100%",
      aspectRatio: "1",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 1,
      cursor: "pointer",
      transition: "all 0.15s ease",
      userSelect: "none",
    };

    if (isSelected) {
      if (present) {
        return {
          ...base,
          backgroundColor: "rgba(76, 175, 80, 0.25)",
          border: "1px solid rgba(76, 175, 80, 0.7)",
          color: "#fff",
          fontWeight: 700,
          "&:hover": { backgroundColor: "rgba(76, 175, 80, 0.35)" },
        };
      }

      if (absent) {
        return {
          ...base,
          backgroundColor: "rgba(244, 67, 54, 0.25)",
          border: "1px solid rgba(244, 67, 54, 0.7)",
          color: "#fff",
          fontWeight: 700,
          "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.35)" },
        };
      }

      return {
        ...base,
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        fontWeight: 700,
        "&:hover": { opacity: 0.9 },
      };
    }

    if (present) {
      return {
        ...base,
        backgroundColor: "rgba(76, 175, 80, 0.15)",
        border: "1px solid rgba(76, 175, 80, 0.4)",
        color: theme.palette.text.primary,
        "&:hover": { backgroundColor: "rgba(76, 175, 80, 0.25)" },
      };
    }

    if (absent) {
      return {
        ...base,
        backgroundColor: "rgba(244, 67, 54, 0.12)",
        border: "1px solid rgba(244, 67, 54, 0.35)",
        color: theme.palette.text.primary,
        "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.2)" },
      };
    }

    if (isToday) {
      return {
        ...base,
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.text.primary,
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
        },
      };
    }

    return {
      ...base,
      border: `1px solid transparent`,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        border: `1px solid ${theme.palette.divider}`,
      },
    };
  };

  return (
    <Box>
      {/* ─── Header: Title + Navigation ─── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Attendance
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" onClick={handlePrevMonth}>
            <ChevronLeft />
          </IconButton>
          <Button
            size="small"
            variant="text"
            onClick={handleToday}
            sx={{
              textTransform: "none",
              color: theme.palette.text.secondary,
              fontSize: "0.8rem",
              minWidth: "auto",
              px: 1,
            }}
          >
            Today
          </Button>
          <IconButton size="small" onClick={handleNextMonth}>
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {/* ─── Month/Year label ─── */}
      <Typography
        variant="subtitle2"
        align="center"
        sx={{ mb: 1.5, color: theme.palette.text.secondary }}
      >
        {currentMonth.format("MMMM YYYY")}
      </Typography>

      {/* ─── Weekday headers ─── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          mb: 0.5,
        }}
      >
        {WEEKDAYS.map((day, i) => (
          <Typography
            key={i}
            variant="caption"
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              py: 0.5,
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* ─── Calendar grid ─── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
        }}
      >
        {calendarDays.map((date, index) => {
          if (!date) {
            return <Box key={`empty-${index}`} />;
          }

          const present = isPresent(date);
          const absent = isAbsent(date);

          return (
            <Box
              key={date.format("YYYY-MM-DD")}
              onClick={() => handleDayClick(date)}
              sx={getDayCellSx(date)}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, lineHeight: 1, fontSize: "0.95rem" }}
              >
                {date.date()}
              </Typography>

              {/* Present checkmark indicator */}
              {present && (
                <Check
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    fontSize: 12,
                    color: "#4caf50",
                  }}
                />
              )}

              {absent && (
                <Close
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    fontSize: 12,
                    color: "#f44336",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* ─── Action bar ─── */}
      {selectedDate && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Mark attendance for {selectedDate.format("MMM D, YYYY")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Check />}
              onClick={handleMarkPresent}
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#4caf50",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#388e3c" },
              }}
            >
              Present
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleMarkAbsent}
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#f44336",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              ✕ Absent
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AttendanceCalendar;
